/* eslint-disable @typescript-eslint/no-explicit-any */
import { FC, useCallback, useEffect, useState } from 'react'
import { cn } from '@bem-react/classname'
import { Helmet } from 'react-helmet'
import { useTranslation } from 'react-i18next'
import { loadStripe, Stripe } from '@stripe/stripe-js'
import { Elements } from '@stripe/react-stripe-js'
import { useNavigate } from 'react-router-dom'

import { Memo } from '@/hoc/Memo'
import { useAppDispatch } from '@/core/store/hooks'
import { changeLoading } from '@/core/services/Loading/store/loading.slice'
import { SuccessType } from './models/LicensesModel'

import { Back } from '@/components/Back'
import { Title } from '@/components/Title'
import { Block } from '@/components/Block'
import { PaymentForm } from './components/PaymentForm'
import { Modal } from '@/components/Modal'

import './styles/PaymentPage.scss'

const stripeApiKey = process.env.REACT_APP_STRIPE_API_KEY || ''

const cnPayment = cn('Payment')

const RETRY_COUNT = 3
const RETRY_DELAY_MS = 2000

async function loadStripeWithRetry(
  retries = RETRY_COUNT,
  delay = RETRY_DELAY_MS,
) {
  for (let i = 0; i <= retries; i++) {
    try {
      const result = await loadStripe(stripeApiKey)
      if (result) {
        return result
      }
    } catch (e) {
      console.error(e)
    }

    await new Promise(resolve => setTimeout(resolve, delay))
  }
  return null
}

export const PaymentPage: FC = Memo(() => {
  const { t } = useTranslation('translation')
  const dispatch = useAppDispatch()
  const navigate = useNavigate()

  const [success, setSuccess] = useState<SuccessType | null>(null)
  const [stripe, setStripe] = useState<Stripe | null>(null)

  const loadStripe = async () => {
    dispatch(changeLoading(true))

    const stripeInstance = await loadStripeWithRetry()
    setStripe(stripeInstance)
    dispatch(changeLoading(false))
  }

  useEffect(() => {
    dispatch(changeLoading(true))
    loadStripe()
  }, [])

  const onReadyCardElement = useCallback(() => {
    dispatch(changeLoading(false))
  }, [])

  const goToListLicenses = useCallback(() => {
    navigate('/cabinet/licenses')
  }, [])

  const handlePaymentSuccess = (successType: SuccessType) => {
    setSuccess(successType)
  }

  return (
    <>
      <Helmet>
        <title>{t('Payment')} | Registration</title>
      </Helmet>
      <div className={cnPayment()}>
        <Back />

        <div className={cnPayment('wrap')}>
          <Block className={cnPayment('block')}>
            <Title className={cnPayment('title')}>{t('By Card')}</Title>
            <div className={cnPayment('text')}>
              {t('Use your personal Card')}
            </div>
            {!stripe ? (
              <div className={cnPayment('error')}>
                {t(
                  'Payment service is currently unavailable. Please check your internet connection, disable any ad-blockers or extensions, and refresh the page',
                )}
              </div>
            ) : (
              <Elements stripe={stripe}>
                <PaymentForm
                  onReadyCardElement={onReadyCardElement}
                  success={handlePaymentSuccess}
                />
              </Elements>
            )}
          </Block>
        </div>
      </div>

      <Modal
        isOpen={!!success}
        title={t('Payment was successful')}
        isInform
        titlePosition='center'
        buttonPosition='center'
        overlayTransparent
        closeBtnText={t('Сontinue')}
        onClose={goToListLicenses}
      >
        <div className={cnPayment('text', { center: true })}>
          {success === SuccessType.SUCCESS_NEW
            ? t(
                'Your activation is in progress! Please check your email, you will receive additional instruction to access your DTM account shortly. Inside your register account, you will have the possibility to renew your license.',
              )
            : t(
                'Your update is in progress! Please check your email, you will receive additional instruction to access your DTM account shortly. Inside your register account, you will have the possibility to renew your license.',
              )}
        </div>
      </Modal>
    </>
  )
})
